import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EmptyFunction, Guid, Nillable } from '@web/utils';
import { Button } from 'primeng/button';
import { PrimengButtonDirective } from '@web/shared/ui';
import { AdminCosmeticCollectionItem } from '@web/shared/module/cosmetic';
import { AdminBoxSaleCosmeticSelectModalComponent } from '../admin-box-sale-cosmetic-select-modal/admin-box-sale-cosmetic-select-modal.component';
import { AdminBoxCosmeticsGridComponent } from '../../components/admin-box-cosmetics-grid/admin-box-cosmetics-grid.component';
import { AdminBoxCosmetic } from '../../models/admin-box-cosmetic';

@Component({
  selector: 'app-admin-box-cosmetics-control',
  imports: [
    AdminBoxCosmeticsGridComponent,
    AdminBoxSaleCosmeticSelectModalComponent,
    Button,
    PrimengButtonDirective,
  ],
  templateUrl: './admin-box-cosmetics-control.component.html',
  styleUrl: './admin-box-cosmetics-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdminBoxCosmeticsControlComponent),
      multi: true,
    },
  ],
})
export class AdminBoxCosmeticsControlComponent implements ControlValueAccessor {
  // prettier-ignore
  private onChange: (value: Nillable<AdminBoxCosmetic[]>) => void = EmptyFunction;
  private onTouched: () => void = EmptyFunction;

  protected readonly cosmetics = signal<AdminBoxCosmetic[]>([]);
  protected readonly cosmeticModalVisible = signal(false);
  protected readonly isDisabled = signal(false);

  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  public writeValue(value: Nillable<AdminBoxCosmetic[]>): void {
    this.cosmetics.set(value ?? []);
    this.changeDetectorRef.markForCheck();
  }

  public registerOnChange(
    fn: (value: Nillable<AdminBoxCosmetic[]>) => void,
  ): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    this.changeDetectorRef.markForCheck();
  }

  protected excludedIds(): Guid[] {
    return this.cosmetics().map((item) => item.id);
  }

  protected handleRemoveCosmeticClick(id: Guid): void {
    if (this.isDisabled()) {
      return;
    }

    const next = this.cosmetics().filter((item) => item.id !== id);

    this.cosmetics.set(next);
    this.onChange(next);
    this.onTouched();
  }

  protected handleSelectCosmeticsClick(): void {
    if (this.isDisabled()) {
      return;
    }

    this.cosmeticModalVisible.set(true);
    this.onTouched();
  }

  protected handleCosmeticModalCancelEvent(): void {
    this.cosmeticModalVisible.set(false);
  }

  protected handleCosmeticModalSubmitEvent(
    items: AdminCosmeticCollectionItem[],
  ): void {
    const existingIds = new Set(this.cosmetics().map((item) => item.id));
    const next = [
      ...this.cosmetics(),
      ...items
        .filter((item) => !existingIds.has(item.id))
        .map(
          (item) =>
            new AdminBoxCosmetic({
              cosmeticBrandName: item.cosmeticBrandName,
              featureFileAlt: item.featuredFileAlt,
              featureFileUrl: item.featuredFileUrl,
              id: item.id,
              name: item.name,
            }),
        ),
    ];

    this.cosmetics.set(next);
    this.onChange(next);
    this.handleCosmeticModalCancelEvent();
    this.onTouched();
  }

  protected hasCosmetics(): boolean {
    return this.cosmetics().length > 0;
  }
}
